import React, { useMemo, useState } from "react";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import "./locationActivityLog.scss";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import LabeledTextInput from "../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import Table from "../../ui-components/table/table";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../app/data/common/queryKeys";
import LocationMasterService from "../../app/data/door-master/locationMasterService";
import { LocationActivityLogModel } from "../../app/data/door-master/models";
import getLocationActivityLogColumns from "./locationActivityColumns";
import { STATIC_LOCATION_ACTIVITY_LOG_ACTION_OPTIONS } from "../../slices/location-master/locationActivityLogSlice";

export interface LocationActivityLogProps {
  show: boolean;
  onClose: () => void;
}

interface LocationActivityLogFilters {
  search?: string;
  action?: string;
  dateFrom?: string;
  dateTo?: string;
}

const LocationActivityLog: React.FC<LocationActivityLogProps> = ({ show, onClose }) => {
  const [searchInput, setSearchInput] = useState<string>("");
  const [dateFromInput, setDateFromInput] = useState<string>("");
  const [dateToInput, setDateToInput] = useState<string>("");
  const [actionTypeInput, setActionTypeInput] = useState<XGSSelectOption | null | undefined>(null);

  const [filters, setFilters] = useState<LocationActivityLogFilters>({
    search: undefined,
    action: undefined,
    dateFrom: undefined,
    dateTo: undefined,
  });

  const formatDateForAPI = (dateStr: string | null | undefined): string | undefined => {
    if (!dateStr) return undefined;

    const parts = dateStr.split("/");
    if (parts.length !== 3) return undefined;

    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const {
    data: locationActivityLogData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.LOCATION_ACTIVITY_LOG, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const dateFrom = filters.dateFrom ? formatDateForAPI(filters.dateFrom) : undefined;
      const dateTo = filters.dateTo ? formatDateForAPI(filters.dateTo) : undefined;
      
      return await LocationMasterService.getInstance().getLocationActivityLogList(
        pageParam as number,
        filters.search,
        filters.action,
        dateFrom,
        dateTo
      );
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.last) return undefined;
      return lastPage?.pageable?.pageNumber + 1;
    },
    keepPreviousData: false,
    retry: false,
    enabled: show,
  });

  const items = useMemo(
    () => locationActivityLogData?.pages.flatMap((page) => page.content) ?? [],
    [locationActivityLogData]
  );

  const diffsAfterSplitting = useMemo(() => {
    let diffs: {
      changedAt: string;
      locationCode: string;
      terminalName?: string;
      action: string;
      modifiedBy: string;
      changeDescription: string;
      terminalNumber?: string | number | null;
      airportCode?: string;
    }[] = [];

    if (!items) return [];

    const formatTerminalName = (item: LocationActivityLogModel) => {
      if (item.airportCode && item.terminalName) {
        return `${item.airportCode} - ${item.terminalName}`;
      }
      return item.terminalName || "-";
    };

    items.forEach((item) => {
      let formattedDescription = item.changeDescription;

      if (item.action === "CREATE" || item.action === "CREATED") {
        const firstSentence = item.changeDescription.split(/[.-]/)[0].trim();
        formattedDescription = firstSentence || "Location created";
      } else if (item.action === "DELETE" || item.action === "DELETED" || item.action === "DEACTIVATE") {
        const firstSentence = item.changeDescription.split(/[.-]/)[0].trim();
        formattedDescription = firstSentence || "Location deleted";
      } else if (item.action === "UPDATE" || item.action === "UPDATED") {
        const lines = item.changeDescription.split("\n").filter((line) => line.trim() !== "");

        if (lines.length > 0) {
          lines.forEach((line) => {
            const updatedLine = line.replaceAll("-->", "→");

            const formattedLine = updatedLine.replace(
              /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\[UTC\]/g,
              (match) => {
                const clean = match.replace(/\[.*\]/, "");
                return clean.toUiMonthDateFormat();
              }
            );

            diffs.push({
              changedAt: item.changedAt,
              locationCode: item.locationCode,
              terminalName: formatTerminalName(item),
              action: item.action,
              modifiedBy: item.modifiedBy,
              changeDescription: formattedLine,
              terminalNumber: item.terminalNumber,
              airportCode: item.airportCode,
            });
          });
          return;
        }
      }

      diffs.push({
        changedAt: item.changedAt,
        locationCode: item.locationCode,
        terminalName: formatTerminalName(item),
        action: item.action,
        modifiedBy: item.modifiedBy,
        changeDescription: formattedDescription,
        terminalNumber: item.terminalNumber,
        airportCode: item.airportCode,
      });
    });
    return diffs;
  }, [items]);

  const getActionOptions = () => {
    return (
      STATIC_LOCATION_ACTIVITY_LOG_ACTION_OPTIONS.map(
        (actions): XGSSelectOption => ({
          label: actions.name,
          value: actions.id,
        })
      ) || []
    );
  };

  const actionOptions = useMemo(() => getActionOptions(), []);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput?.trim() || undefined,
      }));
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchInput]);

  React.useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: dateFromInput || undefined,
      dateTo: dateToInput || undefined,
    }));
  }, [dateFromInput, dateToInput]);

  React.useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      action: actionTypeInput?.value || undefined,
    }));
  }, [actionTypeInput]);

  React.useEffect(() => {
    if (!show) {
      setSearchInput("");
      setDateFromInput("");
      setDateToInput("");
      setActionTypeInput(null);
      setFilters({
        search: undefined,
        action: undefined,
        dateFrom: undefined,
        dateTo: undefined,
      });
    }
  }, [show]);

  const onFromDateChanged = (value: string) => {
    setDateFromInput(value || "");
  };

  const onToDateChanged = (value: string) => {
    setDateToInput(value || "");
  };

  const searchRecords = () => {
    setFilters({
      search: searchInput?.trim() || undefined,
      action: actionTypeInput?.value || undefined,
      dateFrom: dateFromInput || undefined,
      dateTo: dateToInput || undefined,
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchRecords();
    }
  };

  const clearSearchRecords = () => {
    setSearchInput("");
    setDateFromInput("");
    setDateToInput("");
    setActionTypeInput(null);
    setFilters({
      search: undefined,
      action: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
  };

  return (
    <SlideOutSidebar
      header="Location Activity Log"
      show={show}
      onClose={onClose}
      size="medium"
      className="xgs-location-activitylog"
    >
      <div className="xgs-location-activitylog__content">
        <div className="xgs-location-activitylog__filters">
          <div className="xgs-location-activitylog__filters-row">
            <div className="filter-field">
              <LabeledTextInput
                label="Search"
                labelMode={LabelModes.column}
                className="xgs-location-activitylog__filters__input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search across all fields"
              />
            </div>

            <div className="filter-field">
              <LabeledDateRangeInput
                label="Dates Range"
                labelMode={LabelModes.column}
                className="xgs-location-activitylog__filters__dates"
                start={dateFromInput}
                end={dateToInput}
                onStartChange={onFromDateChanged}
                onEndChange={onToDateChanged}
              />
            </div>

            <div className="filter-field">
              <LabeledSelectInput
                label="Action Type"
                labelMode={LabelModes.column}
                className="xgs-location-activitylog__filters__select"
                value={actionTypeInput}
                options={actionOptions}
                placeholder="Select All"
                onValueChange={setActionTypeInput}
              />
            </div>
          </div>

          <div className="xgs-location-activitylog__filters-actions">
            <Button theme={ButtonThemes.gray} className="btn-clear" onClick={clearSearchRecords}>
              Clear Filters
            </Button>

            <Button theme={ButtonThemes.blue} className="btn-search" onClick={searchRecords}>
              Search
            </Button>
          </div>
        </div>

        <div className="xgs-location-activitylog__table">
          <Table
            columns={getLocationActivityLogColumns()}
            data={diffsAfterSplitting}
            isLoading={isLoading || (isFetching && isPreviousData)}
            rowHeight={50}
            minTableHeight={200}
            noResultsText="There are no logs available"
            infiniteScroll
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={
              !locationActivityLogData?.pages.some((page) => page?.content?.length !== 50)
            }
            onInfiniteScroll={() => fetchNextPage()}
            sorting={true}
            enableMultiSort={true}
            keepTableOnLoading
          />
        </div>
      </div>
    </SlideOutSidebar>
  );
};

export default LocationActivityLog;
